import { Space, StatusBadge, Text } from '@procurify/ui'

import {
  AmountFraction,
  AmountFractionType,
} from '@webapp/components/AmountFraction'
import { Price } from '@webapp/core/components/Price'
import { type BillItemExceptionReceiveCost } from '@webapp/domain/accountsPayable/entities'
import { useProcurifyIntl } from '@webapp/hooks'
import { BillItemPopoverLayout } from './BillItemPopoverLayout'
import messages from './messages'

/**
 * The 'BillItemExceptionReceiveCostContent' component is responsible for rendering the content of a bill item received cost exception as the content of a WithPopover component.
 *
 * The exception object comes from the validation errors on the BillItem entity
 */

interface BillItemExceptionReceiveCostContentProps {
  exception: BillItemExceptionReceiveCost
  receivedQuantity?: number
  purchasedQuantity?: number
}

export const BillItemExceptionReceiveCostContent = ({
  exception,
  receivedQuantity = null,
  purchasedQuantity = null,
}: BillItemExceptionReceiveCostContentProps) => {
  const intl = useProcurifyIntl()

  const showReceivedQuantity = !!receivedQuantity && !!purchasedQuantity

  const renderPrice = (price: number) => (
    <Price
      mdp
      price={price}
      currency={exception.currencyName}
      fontWeight='normal'
    />
  )

  return (
    <BillItemPopoverLayout
      title={intl.formatMessage(messages.receivedTitle)}
      bodyRows={[
        {
          id: 'received-cost',
          label: intl.formatMessage(messages.receivedLabel),
          valueComponent: (
            <Space size='xsmall' align='end' justify='start'>
              {showReceivedQuantity && (
                <Space.Item>
                  <Space direction='horizontal' size='xsmall'>
                    <Text>{'('}</Text>
                    <AmountFraction
                      type={AmountFractionType.QUANTITY}
                      amount={receivedQuantity}
                      totalAmount={purchasedQuantity}
                    />
                    <Text>{')'}</Text>
                  </Space>
                </Space.Item>
              )}
              <Space.Item>{renderPrice(exception.receivedCost)}</Space.Item>
            </Space>
          ),
        },
        {
          id: 'previously-billed-cost',
          label: intl.formatMessage(messages.previouslyBilledLabel),
          valueComponent: renderPrice(exception.previouslyBilledCost),
        },
      ]}
      footerRows={[
        {
          id: 'billable-cost',
          label: intl.formatMessage(messages.billableLabel),
          valueComponent: renderPrice(exception.billableCost),
        },
        {
          id: 'billing-cost',
          label: intl.formatMessage(messages.billingLabel),
          valueComponent: (
            <Space direction='horizontal' align='center'>
              <StatusBadge
                text={intl.formatMessage(messages.statusOverbilledLabel)}
                type='error'
              />
              {renderPrice(exception.currentlyBilledCost)}
            </Space>
          ),
        },
      ]}
    />
  )
}
